import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingDots, EmptyState } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { getCategoryColor, getCmsColor, getAvatarColor } from '../../utils/categoryColors';
import { fmtCompact, fmtNum } from '../../utils/formatNumbers';
import {
  listChannelCrm,
  patchChannelCrmRecord,
  importChannelCrmCsv,
  refreshChannelCrmThumbnails,
  ChannelCrmRecord,
} from '../../services/channelIntelligenceApi';

const card: React.CSSProperties = {
  background: 'var(--bg-surface-1)', border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-lg)', padding: 12, marginTop: 16,
};
const control: React.CSSProperties = { padding: '7px 11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)', color: 'var(--fg-1)', fontSize: 13 };
const input: React.CSSProperties = { width: '100%', padding: '6px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)', color: 'var(--fg-1)', fontSize: 13 };
const btn: React.CSSProperties = { ...control, cursor: 'pointer', fontWeight: 600 };
const btnPrimary: React.CSSProperties = { ...btn, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };

// ---- small presentational helpers ----

// Tries each candidate image URL in order (stable YouTube avatar first, then the
// expiring Airtable logo), advancing to the next on load error and finally falling
// back to a colored letter tile. Airtable's signed logo URLs 404 within ~a day, so a
// single-source <img> silently decayed to the letter for the whole network.
const ChannelAvatar: React.FC<{ name: string; urls: (string | undefined)[] }> = ({ name, urls }) => {
  const candidates = urls.filter((u): u is string => !!u);
  const candidateKey = candidates.join('|');
  const [idx, setIdx] = useState(0);
  // Reset to the first candidate whenever the URL set changes (e.g. after a refresh).
  useEffect(() => { setIdx(0); }, [candidateKey]);
  const url = candidates[idx];
  if (url) {
    return <img className="ci-crm-avatar" src={url} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setIdx((i) => i + 1)} />;
  }
  return <div className="ci-crm-avatar-fallback" style={{ background: getAvatarColor(name || '?') }}>{(name || '?').charAt(0)}</div>;
};

const CategoryPills: React.FC<{ tags: string[] }> = ({ tags }) => {
  if (!tags.length) return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  return (
    <>
      {tags.map((t) => {
        const c = getCategoryColor(t);
        return <span key={t} className="ci-crm-pill" style={{ background: c.bg, color: c.fg, borderColor: c.border }}>{t}</span>;
      })}
    </>
  );
};

const CmsChip: React.FC<{ slug?: string; label?: string }> = ({ slug, label }) => {
  if (!label && !slug) return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  const c = getCmsColor(slug || 'unknown'); // color by slug; show the human label as text
  return <span className="ci-crm-pill" style={{ background: c.bg, color: c.fg, borderColor: c.border }}>{label || slug}</span>;
};

interface Draft { cms: string; categoryVertical: string; subCategoryFormat: string; agency: string; managers: string }
const toDraft = (r: ChannelCrmRecord): Draft => ({
  cms: r.cms || '',
  categoryVertical: (r.categoryVertical || []).join(', '),
  subCategoryFormat: (r.subCategoryFormat || []).join(', '),
  agency: r.agency || '',
  managers: r.managers || '',
});
const splitMulti = (v: string): string[] => v.split(',').map((s) => s.trim()).filter(Boolean);

const CrmTab: React.FC = () => {
  const { has } = usePermissions();
  const canManage = has('channelIntelligence.manage');

  const [all, setAll] = useState<ChannelCrmRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [cms, setCms] = useState('');

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const [importing, setImporting] = useState(false);
  const [refreshingImages, setRefreshingImages] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { records } = await listChannelCrm({ limit: 2000 });
      setAll(records);
    } catch (e: any) {
      setError(e?.message || 'Failed to load CRM');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const cmsOptions = useMemo(() => {
    const set = new Set<string>();
    all.forEach((r) => { if (r.cms) set.add(r.cms); });
    return Array.from(set).sort();
  }, [all]);
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    all.forEach((r) => (r.categoryVertical || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [all]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cat = category.trim().toLowerCase();
    return all.filter((r) => {
      if (q && !r.displayName.toLowerCase().includes(q) && r.channelId.toLowerCase() !== q) return false;
      if (cat && !(r.categoryVertical || []).some((t) => t.toLowerCase() === cat)) return false;
      if (cms && (r.cms || '') !== cms) return false;
      return true;
    });
  }, [all, query, category, cms]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const beginEdit = (r: ChannelCrmRecord) => {
    setEditing(r.channelId);
    setDraft(toDraft(r));
    setExpanded((prev) => new Set(prev).add(r.channelId));
  };
  const cancelEdit = () => { setEditing(null); setDraft(null); };

  const saveEdit = async (channelId: string) => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const { record } = await patchChannelCrmRecord(channelId, {
        cms: draft.cms,
        categoryVertical: splitMulti(draft.categoryVertical),
        subCategoryFormat: splitMulti(draft.subCategoryFormat),
        agency: draft.agency || undefined,
        managers: draft.managers || undefined,
      });
      setAll((prev) => prev.map((r) => (r.channelId === channelId ? record : r)));
      cancelEdit();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onImport = async (file: File) => {
    setImporting(true);
    setNotice(null);
    setError(null);
    try {
      const res = await importChannelCrmCsv(file);
      setNotice(`Imported ${res.written} channels (${res.skipped} skipped). CMS re-attribution is running in the background.`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRefreshImages = async () => {
    setRefreshingImages(true);
    setNotice(null);
    setError(null);
    try {
      const res = await refreshChannelCrmThumbnails();
      setNotice(`Refreshed channel images: ${res.resolved} resolved, ${res.updated} updated${res.missing ? `, ${res.missing} without a YouTube avatar` : ''}.`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to refresh channel images');
    } finally {
      setRefreshingImages(false);
    }
  };

  const colSpan = canManage ? 7 : 6;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--fg-1)' }}>Channel CRM</h2>
        <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>{all.length} channels</span>
        <div style={{ flex: 1 }} />
        {canManage && (
          <>
            <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onImport(f); }} />
            <button style={btn} disabled={refreshingImages || importing} onClick={onRefreshImages}
              title="Re-resolve each channel's YouTube avatar (Airtable logo links expire)">
              {refreshingImages ? 'Refreshing…' : '⟳ Refresh images'}
            </button>
            <button style={importing ? btn : btnPrimary} disabled={importing || refreshingImages} onClick={() => fileRef.current?.click()}>
              {importing ? 'Importing…' : '⬆ Import CRM CSV'}
            </button>
          </>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--fg-4)', margin: '6px 0 0', lineHeight: 1.5, maxWidth: 860 }}>
        The network's channel directory (Category/Vertical, Sub-Category / Format, CMS, owners) — the source of truth used to
        answer category/format/CMS questions in the Ask tab &amp; main Chat, and to attribute each ad to a CMS.
        {canManage ? ' Re-import the latest Airtable CSV anytime — channels upsert by Channel ID (no duplicates).' : ''}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <input placeholder="🔍  Search channel…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...control, minWidth: 220 }} />
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={control}>
          <option value="">All categories</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={cms} onChange={(e) => setCms(e.target.value)} style={control}>
          <option value="">All CMSes</option>
          {cmsOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {(query || category || cms) && <button style={btn} onClick={() => { setQuery(''); setCategory(''); setCms(''); }}>Clear</button>}
      </div>

      {notice && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--success)' }}>{notice}</div>}
      {error && <div style={{ marginTop: 12, color: 'var(--danger)' }}>{error}</div>}
      {loading && <div style={{ marginTop: 24 }}><LoadingDots /></div>}

      {!loading && !all.length && !error && (
        <div style={{ marginTop: 24 }}>
          <EmptyState title="No CRM data yet"
            subtitle={canManage ? 'Import the Network CRM CSV export to populate channel metadata.' : 'Ask an admin to import the Network CRM CSV.'} />
        </div>
      )}

      {!loading && all.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12, color: 'var(--fg-4)', margin: '2px 4px 8px' }}>Showing {filtered.length} of {all.length}</div>
          <div className="ci-crm-scroll">
            <table className="ci-crm-table">
              <thead><tr>
                <th style={{ width: 28 }}></th>
                <th>Channel</th>
                <th>CMS</th>
                <th>Category / Vertical</th>
                <th>Sub-Category / Format</th>
                <th style={{ textAlign: 'right' }}>Subscribers</th>
                {canManage && <th style={{ width: 96 }}></th>}
              </tr></thead>
              <tbody>
                {filtered.map((r) => {
                  const isEditing = editing === r.channelId && !!draft;
                  const isOpen = expanded.has(r.channelId) || isEditing;
                  return (
                    <React.Fragment key={r.channelId}>
                      <tr className="ci-crm-row">
                        <td>
                          <button className="ci-crm-chevron" aria-label={isOpen ? 'Collapse' : 'Expand'}
                            style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}
                            onClick={() => toggleExpand(r.channelId)}>▶</button>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <ChannelAvatar name={r.displayName} urls={[r.youtubeThumbnailUrl, r.logoUrl]} />
                            <div style={{ minWidth: 0 }}>
                              {r.channelLink
                                ? <a className="ci-crm-name-link" href={r.channelLink} target="_blank" rel="noopener noreferrer">{r.displayName} ↗</a>
                                : <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{r.displayName}</span>}
                              <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                                {r.channelId}{r.manuallyEdited ? <span title="Edited in-app" style={{ marginLeft: 6, color: 'var(--accent)' }}>• edited</span> : null}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td><CmsChip slug={r.cmsSlug} label={r.cms} /></td>
                        <td style={{ maxWidth: 280 }}><CategoryPills tags={r.categoryVertical || []} /></td>
                        <td style={{ maxWidth: 260 }}>
                          {(r.subCategoryFormat || []).length
                            ? <>
                                {(r.subCategoryFormat || []).slice(0, 4).map((t) => <span key={t} className="ci-crm-pill ci-crm-pill--muted">{t}</span>)}
                                {(r.subCategoryFormat || []).length > 4 && <span className="ci-crm-pill ci-crm-pill--muted">+{(r.subCategoryFormat || []).length - 4}</span>}
                              </>
                            : <span style={{ color: 'var(--fg-4)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtCompact(r.subscriberCount)}</td>
                        {canManage && (
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {isEditing
                              ? <button style={{ ...btn, padding: '4px 9px', fontSize: 12 }} disabled={saving} onClick={cancelEdit}>Cancel</button>
                              : <button style={{ ...btn, padding: '4px 9px', fontSize: 12 }} onClick={() => beginEdit(r)}>Edit</button>}
                          </td>
                        )}
                      </tr>

                      {isOpen && (
                        <tr className="ci-crm-detail">
                          <td colSpan={colSpan}>
                            {isEditing ? (
                              <div style={{ padding: '4px 4px 10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                                <label style={{ fontSize: 12 }}><div style={{ color: 'var(--fg-4)', marginBottom: 3 }}>CMS</div>
                                  <input style={input} value={draft!.cms} onChange={(e) => setDraft({ ...draft!, cms: e.target.value })} /></label>
                                <label style={{ fontSize: 12 }}><div style={{ color: 'var(--fg-4)', marginBottom: 3 }}>Category / Vertical (comma-separated)</div>
                                  <input style={input} value={draft!.categoryVertical} onChange={(e) => setDraft({ ...draft!, categoryVertical: e.target.value })} /></label>
                                <label style={{ fontSize: 12 }}><div style={{ color: 'var(--fg-4)', marginBottom: 3 }}>Sub-Category / Format (comma-separated)</div>
                                  <input style={input} value={draft!.subCategoryFormat} onChange={(e) => setDraft({ ...draft!, subCategoryFormat: e.target.value })} /></label>
                                <label style={{ fontSize: 12 }}><div style={{ color: 'var(--fg-4)', marginBottom: 3 }}>Agency</div>
                                  <input style={input} value={draft!.agency} onChange={(e) => setDraft({ ...draft!, agency: e.target.value })} /></label>
                                <label style={{ fontSize: 12 }}><div style={{ color: 'var(--fg-4)', marginBottom: 3 }}>Team / Manager(s)</div>
                                  <input style={input} value={draft!.managers} onChange={(e) => setDraft({ ...draft!, managers: e.target.value })} /></label>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                                  <button style={btnPrimary} disabled={saving} onClick={() => saveEdit(r.channelId)}>{saving ? 'Saving…' : 'Save changes'}</button>
                                  <button style={btn} disabled={saving} onClick={cancelEdit}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {r.channelSummary && <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5, margin: '2px 4px 10px', maxWidth: 900 }}>{r.channelSummary}</div>}
                                <dl className="ci-crm-detail-grid">
                                  <DetailField label="Content Type" value={(r.contentType || []).join(', ')} />
                                  <DetailField label="Audience / Appeal" value={r.audienceAppeal} />
                                  <DetailField label="Type of Creator" value={r.typeOfCreator} />
                                  <DetailField label="Agency" value={r.agency} />
                                  <DetailField label="Team / Manager(s)" value={r.managers} />
                                  <DetailField label="Brand Safety" value={r.brandSafety} />
                                  <DetailField label="Location" value={r.location} />
                                  <DetailField label="Contract Status" value={r.contractStatus} />
                                  <DetailField label="Spanish spoken?" value={r.spanishSpoken} />
                                  <DetailField label="Subscribers" value={typeof r.subscriberCount === 'number' ? fmtNum(r.subscriberCount) : undefined} />
                                  <DetailField label="Views" value={typeof r.views === 'number' ? fmtNum(r.views) : undefined} />
                                  <DetailField label="Top Performing Format" value={r.topPerformingFormat} />
                                </dl>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!filtered.length && (
                  <tr><td colSpan={colSpan} style={{ textAlign: 'center', color: 'var(--fg-4)', padding: 24 }}>No channels match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailField: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  if (!value) return null;
  return (<div><dt>{label}</dt><dd>{value}</dd></div>);
};

export default CrmTab;
